// Local-only client cart. No external cart API — items live in localStorage
// and go straight to Razorpay Magic Checkout, which posts to
// /api/public/razorpay/create-magic-order + /verify-payment. Those endpoints
// resolve the variantId against variants.shopify_id in Postgres and write the
// paid order to the orders/order_items/payments tables.
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export interface CartItem {
  /** Kept for backwards-compat with old persisted carts; always null now. */
  lineId: string | null;
  /** Legacy GID stored on variants.shopify_id — used as the cart line key. */
  variantId: string;
  productHandle: string;
  productTitle: string;
  imageUrl: string | null;
  price: { amount: string; currencyCode: string };
  quantity: number;
}

export interface AppliedCoupon {
  code: string;
  discountPercent: number;
  prepaidOnly: boolean;
  expiresAt: string;
  discountType?: "percent" | "flat";
  /** Rupees, when discountType === "flat". */
  discountAmount?: number;
  /** Rupees, optional cap for percentage coupons. */
  maxDiscountAmount?: number | null;
}


interface CartState {
  items: CartItem[];
  /** Kept null for compatibility with older callers. */
  cartId: string | null;
  checkoutUrl: string | null;
  isLoading: boolean;
  isSyncing: boolean;
  appliedCoupon: AppliedCoupon | null;
  addItems: (newItems: Omit<CartItem, "lineId">[]) => Promise<string | null>;
  updateQuantity: (variantId: string, quantity: number) => Promise<void>;
  removeItem: (variantId: string) => Promise<void>;
  clearCart: () => void;
  syncCart: () => Promise<void>;
  setCoupon: (coupon: AppliedCoupon | null) => void;
  /** Restore a saved cart silently — no analytics, no re-snapshot. */
  hydrateItems: (items: CartItem[]) => void;
}

async function fireAddToCartMeta(newItems: Omit<CartItem, "lineId">[]) {
  try {
    const { trackAddToCart } = await import("@/lib/analytics/ecommerce");
    trackAddToCart(
      newItems.map((i) => ({
        id: i.productHandle,
        name: i.productTitle,
        price: Number(i.price?.amount ?? 0),
        quantity: i.quantity,
      })),
    );
  } catch {
    /* analytics never blocks cart */
  }
}

// Debounced cart snapshot for the engagement engine's abandoned-cart
// scanner. Fire-and-forget; never blocks the UI, never surfaces errors.
let snapshotTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleCartSnapshot(items: CartItem[]) {
  if (typeof window === "undefined") return;
  if (snapshotTimer) clearTimeout(snapshotTimer);
  snapshotTimer = setTimeout(async () => {
    try {
      const { getAnonymousId } = await import("@/lib/identity/anonymousId");
      const anonymousId = getAnonymousId();
      if (!anonymousId) return;
      const canonical = items
        .filter((i) => i.variantId && i.productHandle && i.quantity > 0)
        .map((i) => ({
          handle: i.productHandle,
          variantId: i.variantId,
          qty: Math.min(20, i.quantity),
        }));
      const { snapshotClientCart } = await import("@/lib/cart-tracking.functions");
      await snapshotClientCart({ data: { anonymousId, items: canonical } });

      // Durable per-device copy so the cart survives a cookie / storage wipe.
      try {
        const { getDeviceIdWhenIdle } = await import("@/lib/identity/deviceFingerprint");
        const deviceId = await getDeviceIdWhenIdle();
        if (!deviceId) return;
        const { saveDeviceCart } = await import("@/lib/device-identity.functions");
        await saveDeviceCart({
          data: {
            deviceId,
            anonymousId,
            items: items
              .filter((i) => i.variantId && i.productHandle && i.quantity > 0)
              .map((i) => ({
                variantId: i.variantId,
                productHandle: i.productHandle,
                productTitle: i.productTitle.slice(0, 160),
                imageUrl: i.imageUrl ? i.imageUrl.slice(0, 600) : null,
                priceAmount: String(i.price?.amount ?? "0"),
                currencyCode: i.price?.currencyCode ?? "INR",
                quantity: Math.min(20, i.quantity),
              })),
          },
        });
      } catch {
        /* device cart storage is best-effort */
      }
    } catch {
      /* engagement never blocks cart */
    }
  }, 2000);
}

/** Fire-and-forget intent signal against the recognised device. */
async function recordIntent(
  signal: "add_to_cart" | "checkout_started",
  handle: string | null,
) {
  try {
    const { getDeviceIdWhenIdle } = await import("@/lib/identity/deviceFingerprint");
    const deviceId = await getDeviceIdWhenIdle();
    if (!deviceId) return;
    const { recordDeviceIntent } = await import("@/lib/device-identity.functions");
    await recordDeviceIntent({ data: { deviceId, signal, handle } });
  } catch {
    /* intent tracking never blocks the cart */
  }
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      cartId: null,
      checkoutUrl: null,
      isLoading: false,
      isSyncing: false,
      appliedCoupon: null,
      setCoupon: (coupon) => set({ appliedCoupon: coupon }),

      hydrateItems: (restored) => {
        if (restored.length === 0) return;
        if (get().items.length > 0) return;
        set({ items: restored });
      },

      addItems: async (newItems) => {
        if (newItems.length === 0) return null;
        set({ isLoading: true });
        try {
          const { items } = get();
          const map = new Map(items.map((i) => [i.variantId, i]));
          for (const item of newItems) {
            const existing = map.get(item.variantId);
            if (existing) {
              map.set(item.variantId, {
                ...existing,
                quantity: existing.quantity + item.quantity,
              });
            } else {
              map.set(item.variantId, { ...item, lineId: null });
            }
          }
          set({ items: Array.from(map.values()) });
          await fireAddToCartMeta(newItems);
          scheduleCartSnapshot(get().items);
          void recordIntent("add_to_cart", newItems[0]?.productHandle ?? null);
          // Return a truthy value so existing callers that check for a
          // checkout URL keep working — the actual checkout is launched
          // separately via launchMagicCheckout.
          return "local";
        } finally {
          set({ isLoading: false });
        }
      },

      updateQuantity: async (variantId, quantity) => {
        if (quantity <= 0) return get().removeItem(variantId);
        set({
          items: get().items.map((i) =>
            i.variantId === variantId ? { ...i, quantity } : i,
          ),
        });
        scheduleCartSnapshot(get().items);
      },

      removeItem: async (variantId) => {
        const remaining = get().items.filter((i) => i.variantId !== variantId);
        if (remaining.length === 0) {
          get().clearCart();
        } else {
          set({ items: remaining });
          scheduleCartSnapshot(remaining);
        }
      },

      clearCart: () => {
        set({ items: [], cartId: null, checkoutUrl: null, appliedCoupon: null });
        scheduleCartSnapshot([]);
      },

      // No external cart to sync anymore; kept as a no-op so existing
      // consumers (CartDrawer opens on mount) don't break.
      syncCart: async () => {},
    }),
    {
      name: "truwe-cart",
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        items: s.items,
        appliedCoupon: s.appliedCoupon,
      }),
    },
  ),
);
